import { useQuery } from '@tanstack/react-query';
import type { inferInput, inferOutput } from '@trpc/tanstack-react-query';
import { useMemo, useState } from 'react';
import {
  COMPETITION_FILTER_INDEX,
  COMPETITION_FILTER_KEYS,
  type CompetitionFilterKey,
} from '../components/filters/competitionFilterCatalog';
import { hasAccounts } from './filters.hooks';
import { useTRPC } from './trpc';

type TRPC = ReturnType<typeof useTRPC>;
type CompetitionPanelInput = inferInput<TRPC['filters']['competitionPanel']>;
type CompetitionNamesInput = inferInput<TRPC['filters']['competitionNames']>;
export type CompetitionPanelDimensions = CompetitionPanelInput['dimensions'];
export type CompetitionSelectedFilters = CompetitionPanelInput['selected'];
export type CompetitionPanelValues = inferOutput<TRPC['filters']['competitionPanel']>;
export type CompetitionDateWindow = CompetitionNamesInput['dateWindow'];

const CREATIVE_ID_SEARCH_DEBOUNCE_MS = 300;

export function useCompetitionPanelValues(
  dimensions: CompetitionPanelDimensions,
  selected?: CompetitionSelectedFilters,
  dateWindow?: CompetitionDateWindow,
  accountIds?: number[],
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.filters.competitionPanel.queryOptions({
      dimensions,
      selected,
      dateWindow,
      accountIds,
    }),
    enabled: dimensions.length > 0 && hasAccounts(accountIds),
    placeholderData: (previous) => previous,
  });
}

export function useCompetitionCreativeIds(
  dateWindow: CompetitionDateWindow,
  accountIds?: number[],
) {
  const trpc = useTRPC();
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const query = useQuery({
    ...trpc.filters.competitionNames.queryOptions({
      dateWindow,
      ...(debouncedSearch ? { nameSearch: debouncedSearch } : {}),
      accountIds,
    }),
    enabled: hasAccounts(accountIds),
  });

  const onSearchChange = useMemo(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    return (nextSearch: string) => {
      clearTimeout(timer);
      timer = setTimeout(() => setDebouncedSearch(nextSearch), CREATIVE_ID_SEARCH_DEBOUNCE_MS);
    };
  }, []);

  return { ...query, onSearchChange };
}

export function availableCompetitionKeys(
  panelValues: CompetitionPanelValues | undefined,
  activeKeys: Set<CompetitionFilterKey> = new Set(),
): Set<CompetitionFilterKey> {
  return new Set(
    COMPETITION_FILTER_KEYS.filter((key) => {
      const dependsOn = COMPETITION_FILTER_INDEX[key].dependsOn;
      if (dependsOn !== undefined && !activeKeys.has(dependsOn)) return true;

      const bucket = panelValues?.[key];
      return bucket === undefined || !bucket.unavailable;
    }),
  );
}
